import React from 'react';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription
} from '@vehix/portal/components/ui/card';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Button } from '@vehix/portal/components/ui/button';
import {
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Clock,
  Play,
  Settings,
  MoreHorizontal
} from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';

export interface WorkflowStat {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

interface WorkflowDefinitionCardProps {
  stepNumber: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  status?: 'idle' | 'running' | 'success' | 'partial_success' | 'failed' | 'cancelled';
  activeRuns?: number;
  lastRunDate?: Date;
  stats?: WorkflowStat[];
  actions?: React.ReactNode;
  className?: string;
  onRun?: () => void;
  onConfigure?: () => void;
  isRunDisabled?: boolean;
  isConfigureDisabled?: boolean;
  runLabel?: string;
}

export const WorkflowDefinitionCard: React.FC<WorkflowDefinitionCardProps> = ({
  stepNumber,
  title,
  description,
  icon,
  status = 'idle',
  activeRuns = 0,
  lastRunDate,
  stats = [],
  actions,
  className,
  onRun,
  onConfigure,
  isRunDisabled = false,
  isConfigureDisabled = false,
  runLabel = 'Run Now'
}) => {
  // Determine status badge
  const getStatusBadge = () => {
    if (activeRuns > 0) {
      return (
        <Badge className='bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 hover:bg-blue-500/20 animate-pulse'>
          <RefreshCw className='h-3 w-3 mr-1 animate-spin' />
          {activeRuns} Running
        </Badge>
      );
    }

    switch (status) {
      case 'success':
        return (
          <Badge className='bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20 hover:bg-green-500/20'>
            <CheckCircle className='h-3 w-3 mr-1' />
            Last Run: Success
          </Badge>
        );
      case 'partial_success':
        return (
          <Badge className='bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20 hover:bg-amber-500/20'>
            <AlertCircle className='h-3 w-3 mr-1' />
            Last Run: Completed with errors
          </Badge>
        );
      case 'failed':
        return (
          <Badge className='bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20 hover:bg-red-500/20'>
            <AlertCircle className='h-3 w-3 mr-1' />
            Last Run: Failed
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className='bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20 hover:bg-slate-500/20'>
            <AlertCircle className='h-3 w-3 mr-1' />
            Last Run: Cancelled
          </Badge>
        );
      case 'running':
        return (
          // Should be covered by activeRuns check, but fallback
          <Badge className='bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 hover:bg-blue-500/20 animate-pulse'>
            <RefreshCw className='h-3 w-3 mr-1 animate-spin' />
            Running
          </Badge>
        );
      default:
        return (
          <Badge variant='outline' className='text-muted-foreground'>
            Ready
          </Badge>
        );
    }
  };

  return (
    <Card
      className={cn('flex flex-col h-full border-l-4', className, {
        'border-l-blue-500': activeRuns > 0 || status === 'running',
        'border-l-green-500': status === 'success' && activeRuns === 0,
        'border-l-amber-500': status === 'partial_success' && activeRuns === 0,
        'border-l-red-500': status === 'failed' && activeRuns === 0,
        'border-l-slate-500': status === 'cancelled' && activeRuns === 0,
        'border-l-muted': status === 'idle' && activeRuns === 0
      })}
    >
      <CardHeader className='pb-4'>
        <div className='flex items-start justify-between'>
          <div className='flex items-center gap-4'>
            <div className='flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold text-lg border border-primary/20 shadow-sm shrink-0'>
              {stepNumber}
            </div>
            <div>
              <CardTitle className='text-lg flex items-center gap-2'>{title}</CardTitle>
              <CardDescription className='mt-1 line-clamp-2'>{description}</CardDescription>
            </div>
          </div>
          <div className='shrink-0 ml-2'>{getStatusBadge()}</div>
        </div>
      </CardHeader>

      <CardContent className='flex-1 pb-4'>
        <div className='space-y-3'>
          {/* Stats Grid */}
          {stats.length > 0 && (
            <div className='grid grid-cols-2 gap-3'>
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={cn(
                    'p-3 bg-muted/30 rounded-lg border border-border/50',
                    stat.className
                  )}
                >
                  <div className='text-xs text-muted-foreground flex items-center mb-1'>
                    {stat.icon && <span className='mr-1.5'>{stat.icon}</span>}
                    {stat.label}
                  </div>
                  <div className='font-semibold text-sm truncate'>{stat.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Last Run Info */}
          {lastRunDate && (
            <div className='flex items-center text-xs text-muted-foreground pt-2 border-t mt-3'>
              <Clock className='h-3 w-3 mr-1.5' />
              Last executed: {lastRunDate.toLocaleString()}
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className='pt-0 flex gap-2'>
        {actions ? (
          actions
        ) : (
          <>
            {onConfigure && (
              <Button
                variant='outline'
                className='flex-1'
                onClick={onConfigure}
                disabled={isConfigureDisabled}
              >
                <Settings className='h-4 w-4 mr-2' />
                Configure
              </Button>
            )}
            {onRun && (
              <Button className='flex-1' onClick={onRun} disabled={isRunDisabled}>
                <Play className='h-4 w-4 mr-2' />
                {runLabel}
              </Button>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
};
